import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import { showAlert } from 'actions/app';
import { StatusManager } from 'common/components/StatusManager';
import { Chart } from 'common/components/ThemedChart';
import { THEME } from 'GlobalStyles';
import moment from 'moment';
import { EntityRoutes } from 'routes/constants';
import { getBulkStats } from 'services/bulkEmail/stats';
import strings from 'strings';
import FPIconButton from 'UI/components/atoms/FPIconButton';
import Text from 'UI/components/atoms/Text';
import BulkStatsInfoCards from 'UI/components/molecules/BulkStatsInfoCards';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import InfoLabel from 'UI/components/molecules/InfoLabel';
import FPDrawer from 'UI/components/templates/FPDrawer/FPDrawer';
import { BulkScopes } from 'UI/constants/defaults';
import { UIStatus } from 'UI/constants/status';
import { SvgArrowLeft } from 'UI/res';
import { dateFormatter } from 'UI/utils';

import StatsInfoAlert from './Components/StatsInfoAlert';
import StatsListing from './StatsListing';
import { useStyles } from './styles';

const {
  shared: sharedCopies,
  bulkEmails: {
    stats: {
      title,
      alerts: alertsCopies,
      details: detailsCopies,
      resume: { openRate }
    }
  }
} = strings;

const EMAIL_FREQUENCY_URL =
  window.GPAC_ENV?.SUPPORT_EMAIL_FREQUENCY_URL || process.env.REACT_APP_SUPPORT_EMAIL_FREQUENCY_URL;

const getFormattedDate = date => moment(date).fromNow();

/**
 * @typedef {Object} BulkStatsProps
 * @property {string|number} bulkId
 * @property {string|number} bulkTypeId
 * @property {Function} onClose
 * @property {string} userEmail
 */

/**
 * @component
 * @param {BulkStatsProps} props
 * @returns {JSX.Element}
 */
const BulkStatsDrawer = ({ bulkId, bulkTypeId, onClose, userEmail }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const [metricToShow, setMetricToShow] = useState(null);

  const [uiState, setUiState] = useState({
    isQuickViewActive: false,
    shouldFetchStats: true,
    showDetails: false
  });
  const [stats, setStats] = useState(null);

  const searchProject = stats?.sentSearchProjectInfo?.searchProject;
  const formattedDate = getFormattedDate(stats?.sentSearchProjectInfo?.send_date);

  useEffect(() => {
    const shouldGetStats = uiState.shouldFetchStats && !uiState.showDetails;

    if (!shouldGetStats) return;
    (async () => {
      const { alert, data, success } = await getBulkStats(bulkId);

      if (success) {
        setStats(data);
        setUiState(prev => ({ ...prev, shouldFetchStats: false }));
      } else {
        dispatch(showAlert(alert));
      }
    })();
  }, [bulkId, dispatch, uiState.shouldFetchStats, uiState.showDetails]);

  const handleGoBack = e => {
    e.preventDefault();

    setUiState({ isQuickViewActive: false, showDetails: false, shouldFetchStats: true });
    setMetricToShow(null);
  };

  const bulkDetails = [
    {
      section: detailsCopies.sections.from,
      value: userEmail
    },
    {
      section: detailsCopies.sections.searchProject,
      mode: 'localLink',
      value: searchProject?.name || '',
      itemId: searchProject?.id,
      linkProps: {
        url: EntityRoutes.SearchProjectsPreview,
        type: 'local',
        isRoute: true,
        className: classes.searchProjectLink
      }
    },
    {
      section: detailsCopies.sections.bulkType,
      value: BulkScopes.ById[bulkTypeId] || ''
    },
    {
      section: detailsCopies.sections.sentDate,
      value: stats?.sentSearchProjectInfo?.send_date
        ? dateFormatter(stats?.sentSearchProjectInfo?.send_date, format => format.MonthDayYearTime)
        : '- -'
    }
  ];

  const chartProps = useMemo(() => {
    const openPercentage = Number(stats?.metricsSummary?.open_ratio.toFixed(2));
    const unopenedPercentage = 100 - stats?.metricsSummary?.open_ratio?.toFixed(2);
    const hasOneSliceOnly = openPercentage === 100 || unopenedPercentage === 100;
    return {
      data: [
        ['Status', 'Emails'],
        ['Open', openPercentage],
        ['Unopened', unopenedPercentage]
      ],
      options: {
        pieHole: 0.4,
        pieSliceTextStyle: {
          color: hasOneSliceOnly && THEME.palette.primary.light
        },
        is3d: false,
        tooltip: { trigger: 'none' },
        backgroundColor: THEME.palette.secondary.FP100,
        slices: {
          0: { color: THEME.palette.primary.light },
          1: { color: THEME.palette.primary.FP100 }
        }
      }
    };
  }, [stats]);

  const handleMetricToShow = selectedMetric => {
    setUiState(prev => ({ ...prev, showDetails: true }));
    setMetricToShow(selectedMetric);
  };

  const toggleQuickViewActive = isActive =>
    setUiState(prev => ({ ...prev, isQuickViewActive: isActive }));

  const defaultHeaderProps = {
    avatar: (
      <FPIconButton
        onClick={handleGoBack}
        iconMode="custom"
        icon={SvgArrowLeft}
        tooltipProps={{ title: 'Back' }}
      />
    )
  };

  const buttonDefinitions = [
    {
      props: {
        text: strings.shared.ui.close,
        variant: 'outlined',
        onClick: onClose
      }
    },
    {
      props: {
        text: sharedCopies.ui.goBack,
        variant: 'contained',
        onClick: handleGoBack
      },
      mode: 'default'
    }
  ];

  const status = stats ? UIStatus.Success : UIStatus.Loading;

  return (
    <FPDrawer
      FPDrawerProps={{ open: true, onClose, classes: { paper: classes.drawerRoot } }}
      CardHeaderProps={{
        title: uiState.showDetails ? metricToShow?.title : title,
        avatar: uiState.showDetails ? defaultHeaderProps.avatar : null,
        titleTypographyProps: { variant: 'h5' }
      }}
      onClose={onClose}
      hasPaddingContent={!uiState.showDetails}
      contentContainerClassName={classes.contentContainer}
      footer={
        metricToShow && (
          <FPButtonBuilder
            GridContainerProps={{ spacing: 2 }}
            buttonDefinitions={buttonDefinitions}
          />
        )
      }
    >
      <StatusManager status={status}>
        {uiState.showDetails ? (
          <StatsListing
            status={metricToShow?.status}
            emailId={bulkId}
            quickViewHandlerProps={{
              onQuickViewClick: toggleQuickViewActive,
              enableNavigationControls: uiState.isQuickViewActive,
              shouldCloseQuickView: uiState.isQuickViewActive
            }}
          />
        ) : (
          <Grid container justify="space-around">
            <Grid item className={classes.infoSnackbar} xs={12}>
              <StatsInfoAlert
                message={strings.formatString(alertsCopies.info.resume.description, {
                  formattedDate
                })}
                url={EMAIL_FREQUENCY_URL}
              />
            </Grid>
            <Grid container item direction="column" xs={4} className={classes.resume}>
              <Grid container item direction="column" className={classes.details}>
                <Typography variant="h5">{detailsCopies.title}</Typography>
                {bulkDetails.map(({ section, value, linkProps, mode, itemId }) => (
                  <Grid key={section} container item direction="column">
                    <InfoLabel
                      title={section}
                      description={value}
                      mode={mode}
                      linkProps={linkProps}
                      itemId={itemId}
                    />
                  </Grid>
                ))}
              </Grid>
              <Grid container item direction="column" className={classes.details}>
                <Typography variant="h5" text={openRate.title}>
                  {openRate.title}
                </Typography>
                <Text variant="body1" text={openRate.description} />
                <Grid container item justify="center" className={classes.chart}>
                  <Chart {...chartProps} chartType="PieChart" width="500px" height="auto" />
                </Grid>
              </Grid>
            </Grid>
            <Grid container item xs={8} className={classes.cardsContainer}>
              {stats && (
                <BulkStatsInfoCards
                  stats={stats.metricsSummary}
                  onShowStatsDetail={handleMetricToShow}
                />
              )}
            </Grid>
          </Grid>
        )}
      </StatusManager>
    </FPDrawer>
  );
};

export default BulkStatsDrawer;
